import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Visibility,
  Person,
  Badge,
  School,
  Business,
  LocalHospital,
} from "@mui/icons-material";
import { apiService } from "../services/api";

export default function UserVerification() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [practiceDecision, setPracticeDecision] = useState(true);
  const [pendingPracticeChanges, setPendingPracticeChanges] = useState([]);
  const [practiceActionLoading, setPracticeActionLoading] = useState(null);

  useEffect(() => {
    fetchPendingPracticeChanges();
  }, []);

  const fetchPendingPracticeChanges = async () => {
    try {
      const data = await apiService.getPendingPracticeChanges();
      setPendingPracticeChanges(data);
    } catch (error) {
      console.error("Failed to fetch pending practice changes:", error);
    }
  };

  const handleApprovePractice = async (organizationId) => {
    setPracticeActionLoading(organizationId);
    try {
      await apiService.approvePracticeChange(organizationId);
      setPendingPracticeChanges((prev) =>
        prev.filter((p) => p.organizationId !== organizationId)
      );
    } catch (error) {
      console.error("Failed to approve practice change:", error);
      setError("Failed to approve practice change");
    } finally {
      setPracticeActionLoading(null);
    }
  };

  const handleRejectPractice = async (organizationId) => {
    setPracticeActionLoading(organizationId);
    try {
      await apiService.rejectPracticeChange(organizationId);
      setPendingPracticeChanges((prev) =>
        prev.filter((p) => p.organizationId !== organizationId)
      );
    } catch (error) {
      console.error("Failed to reject practice change:", error);
      setError("Failed to reject practice change");
    } finally {
      setPracticeActionLoading(null);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPendingUsers();
      setPendingUsers(data);
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
      setError("Failed to load pending users");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
    setPracticeDecision(true);
  };

  const handleApprove = async (userId) => {
    setActionLoading(true);
    try {
      await apiService.approveUser(userId, practiceDecision);
      setPendingUsers(pendingUsers.filter((u) => u.userId !== userId));
      setDetailsOpen(false);
      // Show success message
    } catch (error) {
      console.error("Failed to approve user:", error);
      setError("Failed to approve user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      await apiService.rejectUser(selectedUser.userId, rejectionReason);
      setPendingUsers(
        pendingUsers.filter((u) => u.userId !== selectedUser.userId)
      );
      setRejectDialogOpen(false);
      setDetailsOpen(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Failed to reject user:", error);
      setError("Failed to reject user");
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "Provider":
        return <Badge />;
      case "Researcher":
        return <School />;
      case "Patient":
        return <Person />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "Provider":
        return "success";
      case "Researcher":
        return "info";
      case "Patient":
        return "primary";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading pending users...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        User Verification Dashboard
      </Typography>
      <Typography color="text.secondary" paragraph>
        Review and approve or reject pending healthcare providers and
        researchers
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {pendingUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
          <Typography variant="h6">No Pending Verifications</Typography>
          <Typography color="text.secondary">
            All users have been reviewed
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                <TableCell>
                  <strong>Role</strong>
                </TableCell>
                <TableCell>
                  <strong>Applied</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingUsers.map((user) => (
                <TableRow key={user.userId} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getRoleIcon(user.metadata.role)}
                      label={user.metadata.role}
                      color={getRoleColor(user.metadata.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetails(user)}
                      >
                        Review
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Practice-change requests from already-approved providers */}
      {pendingPracticeChanges.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Pending Practice Changes
          </Typography>
          <Typography color="text.secondary" paragraph>
            Already-approved providers requesting a new practice not yet on our
            verified list
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                  <TableCell><strong>Provider</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Requested Practice</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPracticeChanges.map((change) => (
                  <TableRow key={change.organizationId} hover>
                    <TableCell>{change.providerName}</TableCell>
                    <TableCell>{change.providerEmail}</TableCell>
                    <TableCell>{change.organizationName}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          disabled={practiceActionLoading === change.organizationId}
                          onClick={() => handleRejectPractice(change.organizationId)}
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          disabled={practiceActionLoading === change.organizationId}
                          onClick={() => handleApprovePractice(change.organizationId)}
                        >
                          Approve
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => !actionLoading && setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedUser && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {getRoleIcon(selectedUser.metadata.role)}
                <Box>
                  <Typography variant="h6">{selectedUser.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        gutterBottom
                      >
                        BASIC INFORMATION
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Role
                          </Typography>
                          <Typography variant="body1">
                            {selectedUser.metadata.role}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Applied On
                          </Typography>
                          <Typography variant="body1">
                            {new Date(
                              selectedUser.createdAt
                            ).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Provider Credentials */}
                {selectedUser.metadata.role === "Provider" && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <LocalHospital color="success" />
                          <Typography variant="subtitle2" color="success">
                            PROVIDER CREDENTIALS
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              NPI Number
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.npiNumber}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Medical License
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.medicalLicense}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              License State
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.licenseState}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Specialization
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.specialization}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Practice / Organization
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body1">
                                {selectedUser.metadata.practiceName || "Not provided"}
                              </Typography>
                              {selectedUser.metadata.practiceStatus && (
                                <Chip
                                  size="small"
                                  label={selectedUser.metadata.practiceStatus}
                                  color={
                                    selectedUser.metadata.practiceStatus === "PENDING"
                                      ? "warning"
                                      : "success"
                                  }
                                />
                              )}
                            </Box>
                          </Grid>
                        </Grid>

                        {selectedUser.metadata.practiceStatus === "PENDING" && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <FormLabel sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                              This is a new practice, not yet on our verified list —
                              how should it be handled?
                            </FormLabel>
                            <RadioGroup
                              value={practiceDecision ? "approve" : "default"}
                              onChange={(e) => setPracticeDecision(e.target.value === "approve")}
                            >
                              <FormControlLabel
                                value="approve"
                                control={<Radio size="small" />}
                                label={`Approve "${selectedUser.metadata.practiceName}" as a verified practice`}
                              />
                              <FormControlLabel
                                value="default"
                                control={<Radio size="small" />}
                                label="Not acceptable — assign this provider to the Default Practice instead"
                              />
                            </RadioGroup>
                          </Alert>
                        )}
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption">
                            <strong>Verification Steps:</strong>
                            <br />
                            1. Verify NPI at{" "}
                            <a
                              href={`https://npiregistry.cms.hhs.gov/search?number=${selectedUser.metadata.npiNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              NPPES Registry
                            </a>
                            <br />
                            2. Verify medical license with state board
                            <br />
                            3. Confirm specialization matches NPI record
                          </Typography>
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Researcher Credentials */}
                {selectedUser.metadata.role === "Researcher" && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Business color="info" />
                          <Typography variant="subtitle2" color="info">
                            RESEARCHER CREDENTIALS
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Institution/Organization
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.institution}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Research Area
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.researchArea}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Years of Experience
                            </Typography>
                            <Typography variant="body1">
                              {selectedUser.metadata.yearsExperience ||
                                "Not provided"}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption">
                            <strong>Verification Steps:</strong>
                            <br />
                            1. Verify institutional affiliation
                            <br />
                            2. Check research publications/credentials
                            <br />
                            3. Confirm email domain matches institution
                          </Typography>
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button
                onClick={() => setDetailsOpen(false)}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setRejectDialogOpen(true)}
                disabled={actionLoading}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleApprove(selectedUser.userId)}
                disabled={actionLoading}
              >
                {actionLoading ? <CircularProgress size={24} /> : "Approve"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !actionLoading && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please provide a reason for rejecting this application. The user
            will receive this via email.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Unable to verify credentials, Invalid NPI number, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? (
              <CircularProgress size={24} />
            ) : (
              "Confirm Rejection"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}